import type { Layout } from "react-grid-layout";

import {
  FilterParams,
  PaginatedData,
  PaginatedParams
} from "src/library/utils";
import {
  EndpointConfig,
  RequestConfig,
  UseApiRequest
} from "src/core/shared/api/config.api";
import useAxios from "src/core/shared/hooks/useAxios";
import { ICustomer } from "src/core/customers/api/customers.api";
import { IDrop } from "src/core/drops/api/drops.api";
import cacheMinutesToMilliseconds from "src/library/utils/time-helpers/cache-minutes-to-milliseconds";

import { WidgetSlug } from "./widget-data.api";

type DashboardEndpointConfig = {
  GET_DASHBOARDS: EndpointConfig<
    GetDashboardsParams,
    PaginatedData<IDashboard>
  >;
  CREATE_DASHBOARD: EndpointConfig<
    CreateDashboardParams,
    IDashboard
  >;
  GET_DASHBOARD: EndpointConfig<
    {dashboardId: number},
    IDashboard
  >;
  GET_DEFAULT_DASHBOARD: EndpointConfig<
    {userId: number},
    IDashboard
  >;
  GET_FILTER_TYPES: EndpointConfig<
    null,
    IFilterType[]
  >;
  UPDATE_DASHBOARD: EndpointConfig<
    UpdateDashboardParams,
    IDashboard
  >;
  UPDATE_GLOBAL_DASHBOARD_FILTERS: EndpointConfig<
    UpdateGlobalDashboardFiltersParams,
    IDashboardFilter[]
  >;
  GET_WIDGET_TYPES: EndpointConfig<
    null,
    IWidgetType[]
  >;
  DELETE_DASHBOARD: EndpointConfig<
    {id: number},
    number
  >;
};

const BASE_URL = `${process.env.REACT_APP_API_URL}/core/dashboards`;

const requests: RequestConfig<DashboardEndpointConfig> = {
  GET_DASHBOARDS: (params, cache) => {
    return ({
      ...cache,
      url: `${BASE_URL}`,
      method: "GET",
      params
    });
  },
  CREATE_DASHBOARD: data => ({
    url: `${BASE_URL}`,
    method: "POST",
    data
  }),
  GET_DASHBOARD: data => ({
    url: `${BASE_URL}/${data.dashboardId}`,
    method: "GET"
  }),
  GET_DEFAULT_DASHBOARD: data => ({
    url: `${BASE_URL}/default/${data.userId}`,
    method: "GET"
  }),
  GET_FILTER_TYPES: () => ({
    url: `${BASE_URL}/filter-types/all`,
    method: "GET",
    cache: {
      clearOnError: true,
      maxAge: cacheMinutesToMilliseconds(60)
    }
  }),
  UPDATE_DASHBOARD: data => ({
    url: `${BASE_URL}/${data.id}`,
    method: "PATCH",
    data
  }),
  UPDATE_GLOBAL_DASHBOARD_FILTERS: data => ({
    url: `${BASE_URL}/${data.dashboardId}/global-filters`,
    method: "PUT",
    data: data.params
  }),
  GET_WIDGET_TYPES: () => ({
    url: `${BASE_URL}/widget/types`,
    method: "GET",
    cache: {
      clearOnError: true,
      maxAge: cacheMinutesToMilliseconds(60)
    }
  }),
  DELETE_DASHBOARD: data => ({
    url: `${BASE_URL}/${data.id}`,
    method: "DELETE"
  })
};

export interface IDashboard {
  id: number;
  name: string;
  userId: number;
  layout: UserLayoutPreferences;
  default?: boolean;
  widgets?: IWidget[];
  dashboardFilters?: ModifiedDashboardFilter[];
}

export interface GetDashboardsParams {
  pagination: PaginatedParams<IDashboard>;
  filters: GetDashboardsFilters;
}

export type CreateDashboardParams = Pick<IDashboard, "name">;
export type UpdateDashboardParams = Pick<IDashboard, "id"> & Partial<IDashboard>;
export type UpdateGlobalDashboardFiltersParams = {
  dashboardId: number;
  params: Array<Omit<IDashboardFilter, "id">>
};

export interface IFilterType {
  id: number;
  filterBy: FilterByType;
  filterOptions?: FilterTypeOptions;
}

export interface FilterTypeOptions {
  customer?: { id: number },
  branch?: { id: number },
  dateRange?: FilterTypeOptionDateRange;
}

export interface FilterTypeOptionDateRange {
  days?: number | null,
  custom?: {
    /**YYYY-MM-DD */
    start: string;
    end: string;
  } | null;
}

export type DateRangeOptions = FilterTypeOptions["dateRange"];

export enum FilterByType {
  Customer = "customer",
  Branch = "branch",
  DateRange = "date_range"
}

export interface IWidget {
  id: number | string;
  dashboardId: number;
  widgetTypeId: number;
  widgetType?: IWidgetType;
  options: {view: WidgetDisplayTypes};
  // If exists its new
  uuid?: string;
}

export interface IWidgetType {
  id: number;
  name: string;
  slug: WidgetSlug;
  dataType: WidgetDataTypes;
  options: { view: WidgetDisplayTypes[]; groupByKey?: string; };
  layoutOptions?: FixedLayoutOptions;
}

export enum WidgetDataTypes {
  Orders = "orders",
  Credits = "credits",
  Support = "support",
  Accounting = "accounting"
}

export enum WidgetDisplayTypes {
  Number = "number",
  LineGraph = "line-graph",
  BarGraph = "bar-graph",
  Table = "table"
}

export type LayoutOptions = Pick<Layout, "i" | "x" | "y" | "w" | "h">;
export type LayoutPreferenceScreenSize = LayoutOptions[];
export type UserLayoutPreferences = {
  lg: LayoutPreferenceScreenSize;
  md: LayoutPreferenceScreenSize;
  sm: LayoutPreferenceScreenSize;
 };

export type FixedLayoutOptions = Pick<Layout, "minW" | "maxW" | "minH" | "maxH" | "isResizable" | "isDraggable">;

export interface IDashboardFilter {
  id: number;
  dashboardId: number;
  filterTypeId: number;
  filterValue: {
    customer?: { id: number },
    branch?: { id: number },
    dateRange?: FilterTypeOptionDateRange;
  }
}

// adds data as optional property on customer and branch properties
export interface ModifiedDashboardFilter extends Omit<IDashboardFilter, "filterValue"> {
  filterValue: {
    customer?: { id: number, data?: ICustomer },
    branch?: { id: number, data?: IDrop },
    dateRange?: FilterTypeOptionDateRange;
  }
}

export type GetDashboardsFilters = FilterParams<Omit<IDashboard, "layout">>;

export const useDashboardApi: UseApiRequest<DashboardEndpointConfig> = name => useAxios(requests[ name ], name);
